import { countryMap } from '../../../core/utils/constants/constants';
import { transform, isObject } from 'lodash';
import moment from 'moment';

export const buildGeoTargetPayload = ({ target, orgId }) => {
  const { circles, polygons, location } = target;

  if (location) {
    delete location.id;
    delete location.__typename;
  }

  return {
    circles: {
      create: circles.length
        ? circles.map((circle) => {
            const circlePayload = {
              radius: circle.radius,
              coordinate: {
                create: {
                  lat: circle.coordinate.lat,
                  lng: circle.coordinate.lng,
                },
              },
            };

            if (circle.location) {
              circlePayload.location = {
                create: {
                  name: circle.location?.name || '',
                  formattedAddress: circle.location?.formattedAddress || '',
                  zip: circle.location?.zip || '',
                  street: circle.location?.street || '',
                  city: circle.location?.city || '',
                  lat: circle.location?.lat || 0,
                  lng: circle.location?.lng || 0,
                  ...circle.location,
                },
              };
            }
            return circlePayload;
          })
        : [],
    },
    polygons: {
      create: polygons.length
        ? polygons.map((polygon) => {
            const polygonPayload = {
              geometry: {
                coordinates: polygon.geometry.coordinates,
              },
            };

            if (polygon.location) {
              polygonPayload.location = {
                create: {
                  name: polygon.location?.name || '',
                  formattedAddress: polygon.location?.formattedAddress || '',
                  zip: polygon.location?.zip || '',
                  street: polygon.location?.street || '',
                  city: polygon.location?.city || '',
                  lat: polygon.location?.lat || 0,
                  lng: polygon.location?.lng || 0,
                  ...polygon.location,
                },
              };
            }

            return polygonPayload;
          })
        : [],
    },
    location: {
      create: {
        name: location?.name || '',
        formattedAddress: location?.formattedAddress || '',
        zip: location?.zip || '',
        street: location?.street || '',
        city: location?.city || '',
        lat: location?.lat || 0,
        lng: location?.lng || 0,
        ...location,
      },
    },
    name: target.name || '',
    start: target.start || null,
    end: target.end || null,
    // orgs: {
    //   connect: {
    //     id: orgId,
    //   },
    // },
    status: 'CREATED',
  };
};

export const buildGeoTargetsPayload = ({ targets = [], originalTargets = [], orgId }) => {
  // Find the targets that were deleted
  const deletedTargets = originalTargets.filter(
    (originalTarget) => !targets.some((target) => target.name === originalTarget.name)
  );

  const newTargets = targets.filter(
    (target) => !originalTargets.some((originalTarget) => originalTarget.name === target.name)
  );

  const payload = {
    create: newTargets.map((target) => buildGeoTargetPayload({ target, orgId })),
  };

  if (deletedTargets.length) {
    payload.delete = deletedTargets.map((target) => ({
      id: target.id,
    }));
  }

  return payload;
};

export const transformLocationToGeoTarget = ({ location }) => {
  const {
    name,
    formattedAddress,
    city,
    state,
    zip,
    lat,
    lng,
    street,
    country,
    geoframe,
    org,
    audienceType,
    circles = [],
    polygons = [],
  } = location;

  const geoFrameCircles = geoframe && geoframe.circles ? geoframe.circles : [];
  const geoFramePolygons = geoframe && geoframe.polygons ? geoframe.polygons : [];

  const newCircles = geoFrameCircles.length ? geoFrameCircles : circles;
  const newPolygons = geoFramePolygons.length ? geoFramePolygons : polygons;

  return {
    circles: newCircles.map((circle) => ({
      ...circle,
      location: {
        name: circle.location ? circle.location.name : name,
        formattedAddress: circle.location ? circle.location.formattedAddress : formattedAddress,
        street: circle.location ? circle.location.street : street,
        state: circle.location ? circle.location.state : state,
        city: circle.location ? circle.location.city : city,
        zip: circle.location ? circle.location.zip : zip,
        country: circle.location ? circle.location.country : country,
        lat: circle.location ? circle.location.lat : lat,
        lng: circle.location ? circle.location.lng : lng,
        audienceType,
      },
    })),
    polygons: newPolygons.map((polygon) => ({
      ...polygon,
      location: {
        name: polygon.location ? polygon.location.name : name,
        formattedAddress: polygon.location ? polygon.location.formattedAddress : formattedAddress,
        street: polygon.location ? polygon.location.street : street,
        state: polygon.location ? polygon.location.state : state,
        city: polygon.location ? polygon.location.city : city,
        zip: polygon.location ? polygon.location.zip : zip,
        country: polygon.location ? polygon.location.country : country,
        lat: polygon.location ? polygon.location.lat : lat,
        lng: polygon.location ? polygon.location.lng : lng,
        audienceType,
      },
    })),
    location: {
      name: name,
      formattedAddress: formattedAddress,
      street: street,
      state: state,
      city: city,
      zip: zip,
      country: country,
      lat: lat,
      lng: lng,
      audienceType,
    },
    name: location.name,
    start: null,
    end: null,
    org,
  };
};

export const buildAdGroupPayload = ({ adGroup, orgId, campaign, originalLineItems = [], forceCreate = false }) => {
  let newAdGroupCreatives = [];
  let updatedAdGroupCreatives = [];
  let deletedAdGroupCreatives = [];

  if (adGroup.adGroupCreatives && adGroup.adGroupCreatives.length) {
    newAdGroupCreatives = adGroup.adGroupCreatives.filter((creative) => !creative.id);
    updatedAdGroupCreatives = adGroup.adGroupCreatives.filter((creative) => creative.id);
  }

  const matchedCampaign = originalLineItems
    .filter(
      (campaign) =>
        campaign.adGroup &&
        campaign.adGroup.adGroupCreatives &&
        campaign.adGroup.adGroupCreatives.length
    )
    .find((c) => c.id === campaign.id);

  if (matchedCampaign) {
    deletedAdGroupCreatives = matchedCampaign.adGroup.adGroupCreatives.filter(
      (creative) => !campaign.adGroup.adGroupCreatives.find((c) => c.name === creative.name)
    );
  }

  if (adGroup && adGroup.id && !forceCreate) {
    return {
      update: {
        clickthrough: adGroup.clickthrough,
        adGroupCreatives: {
          create: newAdGroupCreatives.map((adGroupCreative) => {
            return {
              name: adGroupCreative.name,
              clickthrough: adGroupCreative.clickthrough,
              startDate: adGroupCreative.startDate,
              endDate: adGroupCreative.endDate,
              medias: {
                create: adGroupCreative.medias.map((media) => ({
                  fileType: media.fileType,
                  height: media.height,
                  key: media.key,
                  name: media.name,
                  size: media.size,
                  type: media.type,
                  url: media.url,
                  width: media.width,
                })),
              },
            };
          }),
          update: updatedAdGroupCreatives.map((adGroupCreative) => {
            return {
              where: {
                id: adGroupCreative.id,
              },
              data: {
                startDate: adGroupCreative.startDate,
                endDate: adGroupCreative.endDate,
                clickthrough: adGroupCreative.clickthrough,
              },
            };
          }),
          delete: deletedAdGroupCreatives.map((adGroupCreative) => ({
            id: adGroupCreative.id,
          })),
        },
      },
    };
  } else {
    return {
      create: {
        clickthrough: adGroup.clickthrough,
        adGroupCreatives: {
          create:
            adGroup.adGroupCreatives && adGroup.adGroupCreatives.length
              ? adGroup.adGroupCreatives.map((adGroupCreative) => {
                  const medias = Array.isArray(adGroupCreative.medias)
                    ? adGroupCreative.medias
                    : [];
                  return {
                    name: adGroupCreative.name,
                    clickthrough: adGroupCreative.clickthrough,
                    startDate: adGroupCreative.startDate,
                    endDate: adGroupCreative.endDate,
                    medias: {
                      create: medias.map((media) => ({
                        fileType: media.fileType,
                        height: media.height,
                        key: media.key,
                        mediaHasNotes: media.mediaHasNotes,
                        mediaNotes: media.mediaNotes,
                        name: media.name,
                        org: media.org,
                        size: media.size,
                        type: media.type,
                        url: media.url,
                        width: media.width,
                        sharedWith: {
                          connect: {
                            id: orgId,
                          },
                        },
                      })),
                    },
                  };
                })
              : [],
        },
      },
    };
  }
};

export const calculateImpressions = ({ cpm, budget }) => {
  return budget && cpm ? Math.ceil((Number(budget) / Number(cpm)) * 1000) : 0;
};

export const calculateImpressionsByPerecent = ({ cpm, budget, percent }) => {
  return budget && cpm ? Math.ceil((Number(budget) / Number(cpm)) * 1000 * percent) : 0;
};

export const buildLineItemUpdatePayload = ({ lineItem, originalLineItems, selectedAdvertiser }) => {
  const { targetingRegions, targetingLocations, targetingCountries, audienceType } = lineItem;
  let newTargetingRegions = [];
  let updatedTargetingRegions = [];
  let deletedTargetingRegions = [];
  let newTargetingLocations = [];
  let updatedTargetingLocations = [];
  let deletedTargetingLocations = [];
  let newTargetingCountries = [];
  let deletedTargetingCountries = [];

  switch (lineItem.targetingType) {
    case 'REGION':
      newTargetingRegions = targetingRegions.filter((region) => !region.id);
      updatedTargetingRegions = targetingRegions.filter((region) => region.id);
      deletedTargetingRegions = originalLineItems
        .find((c) => c.id === lineItem.id)
        .targetingRegions.filter((region) => !targetingRegions.find((r) => r.name === region.name));

      // Delete all targeting locations
      deletedTargetingLocations = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingLocations;
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;

      break;
    case 'LOCAL':
      newTargetingLocations = targetingLocations
        .filter((location) => !location.id)
        .map((location) => {
          return {
            ...location,
            geoTargets: location.geoTargets.map((target) => {
              delete target.id;
              return target;
            }),
          };
        });
      updatedTargetingLocations = targetingLocations.filter((location) => location.id);
      deletedTargetingLocations = originalLineItems
        .find((c) => c.id === lineItem.id)
        .targetingLocations.filter(
          (location) => !targetingLocations.find((l) => l.name === location.name)
        );

      // Delete all targeting regions
      deletedTargetingRegions = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingRegions;
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;
      break;

    case 'COUNTRY':
      newTargetingCountries = [
        {
          name: countryMap[targetingCountries],
          code: targetingCountries,
        },
      ];

      // Delete all targeting locations
      deletedTargetingLocations = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingLocations;

      // Delete all targeting regions
      deletedTargetingRegions = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingRegions;
      // Delete all targeting countries
      deletedTargetingCountries = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).targetingCountries;
      break;

    default:
      break;
  }

  // Handle audience type
  let newFirstPartyDataAudiences = [];
  let updatedFirstPartyDataAudiences = [];
  let deletedFirstPartyDataAudiences = [];

  switch (audienceType) {
    case 'FIRST_PARTY_DATA':
      newFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter(
        (audience) => !audience.id
      );
      updatedFirstPartyDataAudiences = lineItem.firstPartyDataAudiences.filter(
        (audience) => audience.id
      );
      deletedFirstPartyDataAudiences = originalLineItems
        .find((c) => c.id === lineItem.id)
        .firstPartyDataAudiences.filter(
          (audience) => !lineItem.firstPartyDataAudiences.find((a) => a.name === audience.name)
        );

      break;
    case 'ALL_USERS':
      // Delete all first party data audiences
      deletedFirstPartyDataAudiences = originalLineItems.find(
        (c) => c.id === lineItem.id
      ).firstPartyDataAudiences;

      break;
    default:
      break;
  }

  return {
    where: {
      id: lineItem.id,
    },
    data: {
      name: lineItem.name,
      startDate: lineItem.startDate,
      endDate: lineItem.endDate,
      budgetTotal: Number(lineItem.budgetTotal),
      cpm: Number(lineItem.cpm),
      // Handle Targeting
      targetingType: lineItem.targetingType,
      valueAdd: lineItem.valueAdd,
      targetingRegions: {
        create: newTargetingRegions.map((region) => ({
          name: region.name,
          regions: {
            set: region.regions.map((region) => region),
          },
        })),
        update: updatedTargetingRegions.map((region) => ({
          where: {
            id: region.id,
          },
          data: {
            name: region.name,
            regions: {
              set: region.regions.map((region) => region),
            },
          },
        })),
        delete: deletedTargetingRegions.map((region) => ({
          id: region.id,
        })),
      },
      targetingLocations: {
        create: newTargetingLocations.map((location) => ({
          name: location.name,
          type: location.type,
          geoTargets: buildGeoTargetsPayload({
            targets: location.geoTargets,
            originalTargets: [],
            orgId: selectedAdvertiser.id,
          }),
        })),
        update: updatedTargetingLocations.map((location) => ({
          where: {
            id: location.id,
          },
          data: {
            name: location.name,
            type: location.type,
            geoTargets: buildGeoTargetsPayload({
              targets: location.geoTargets,
              originalTargets: lineItem.targetingLocations.find((l) => l.name === location.name)
                .geoTargets,
              orgId: selectedAdvertiser.id,
            }),
          },
        })),
        delete: deletedTargetingLocations.map((location) => ({
          id: location.id,
        })),
      },
      targetingCountries: targetingCountries
        ? {
            create: newTargetingCountries.map((country) => ({
              name: country.name,
              code: country.code,
            })),
            delete: deletedTargetingCountries.map((country) => ({
              id: country.id,
            })),
          }
        : null,
      // Handle Audiences
      audienceType: lineItem.audienceType,
      firstPartyDataAudiences: {
        create: newFirstPartyDataAudiences.map((audience) => ({
          name: audience.name,
          activated: audience.activated,
          tag: audience.tag,
          deviceCount: audience.deviceCount,
          matchRate: audience.matchRate,
          isProcessedByStatera: audience.isProcessedByStatera,
          dspSegmentIds: {
            set: audience.dspSegmentIds,
          },
        })),
        update: updatedFirstPartyDataAudiences.map((audience) => ({
          where: {
            id: audience.id,
          },
          data: {
            name: audience.name,
            activated: audience.activated,
            tag: audience.tag,
            deviceCount: audience.deviceCount,
            matchRate: audience.matchRate,
            isProcessedByStatera: audience.isProcessedByStatera,
            dspSegmentIds: {
              set: audience.dspSegmentIds,
            },
          },
        })),
        delete: deletedFirstPartyDataAudiences.map((audience) => ({
          id: audience.id,
        })),
      },
      // Handle Ad Group
      adGroup: lineItem.adGroup
        ? buildAdGroupPayload({
            adGroup: lineItem.adGroup,
            orgId: selectedAdvertiser.id,
            campaign: lineItem,
            originalLineItems: originalLineItems,
          })
        : null,
    },
  };
};

export const buildLineItemPayload = ({ lineItem, originalLineItems, selectedAdvertiser, forceCreate = false }) => {
  const { targetingType } = lineItem;
  const adGroup = lineItem.adGroup;

  const newTargetingCountries = [];

  if (lineItem.targetingCountries) {
    newTargetingCountries.push({
      name: countryMap[lineItem.targetingCountries],
      code: lineItem.targetingCountries,
    });
  }

  return {
    name: lineItem.name,
    startDate: lineItem.startDate,
    endDate: lineItem.endDate,
    budgetTotal: Number(lineItem.budgetTotal),
    cpm: Number(lineItem.cpm),
    targetingType,
    valueAdd: lineItem.valueAdd,
    targetingRegions: {
      create:
        targetingType === 'REGION'
          ? (lineItem.targetingRegions || []).map((region) => ({
              name: region.name,
              regions: {
                set: (region.regions || []).map((region) => region),
              },
            }))
          : [],
    },
    targetingCountries: {
      create:
        targetingType === 'COUNTRY'
          ? newTargetingCountries.map((country) => ({
              name: country.name,
              code: country.code,
            }))
          : [],
    },
    targetingLocations: {
      create:
        targetingType === 'LOCAL'
          ? (lineItem.targetingLocations || []).map((location) => ({
              name: location.name,
              type: location.type,
              geoTargets: buildGeoTargetsPayload({
                targets: location.geoTargets || [],
                originalTargets: [],
                orgId: selectedAdvertiser.id,
              }),
            }))
          : [],
    },
    audienceType: lineItem.audienceType,
    firstPartyDataAudiences: {
      create: (lineItem.firstPartyDataAudiences || []).map((audience) => ({
        name: audience.name,
        activated: audience.activated,
        tag: audience.tag,
        deviceCount: audience.deviceCount,
        matchRate: audience.matchRate,
        isProcessedByStatera: audience.isProcessedByStatera,
        dspSegmentIds: {
          set: audience.dspSegmentIds,
        },
      })),
    },
    adGroup: adGroup
      ? buildAdGroupPayload({
          adGroup,
          orgId: selectedAdvertiser.id,
          campaign: lineItem,
          originalLineItems: originalLineItems,
          forceCreate: forceCreate,
        })
      : null,
    adUnits: {
      create: [],
    },
  };
};

export const deleteProperties = ({ object, properties }) => {
  return transform(object, (result, value, key) => {
    if (!properties.includes(key)) {
      // Skip properties in the `properties` array
      // Recursively process nested objects or arrays
      result[key] = isObject(value) ? deleteProperties({ object: value, properties }) : value;
    }
  });
};

// Helper: Validates campaign order level fields only (without campaigns)
const validateCampaignOrderFields = (currentCampaignOrder, message) => {
  const { budget, cpm, startDate, endDate, category, advertiser, advertiserType, campaigns } = currentCampaignOrder;

  // Check if advertiser has a dspAdvertiserId
  if (!advertiser.dataConfig.dspAdvertiserId) {
    message.error('Advertiser must have a DSP advertiser ID. Please check organization settings.');
    return false;
  }

  // Check if budget exists and is a valid positive number
  if (!budget || budget <= 0 || isNaN(budget)) {
    message.error('Campaign order budget must be a valid positive number greater than 0');
    return false;
  }

  // Check if CPM exists and is a valid positive number
  if (!cpm || cpm <= 0 || isNaN(cpm)) {
    message.error('Campaign order CPM must be a valid positive number greater than 0');
    return false;
  }

  // Check if start date exists and is valid
  if (!startDate) {
    message.error('Campaign order start date is required');
    return false;
  }

  // Check if end date exists and is valid
  if (!endDate) {
    message.error('Campaign order end date is required');
    return false;
  }

  // Check if start date is before end date
  if (moment(startDate).isSameOrAfter(moment(endDate))) {
    message.error('Campaign order start date must be before end date');
    return false;
  }

  // Check if category exists (can be array or single value)
  if (!category || (Array.isArray(category) && category.length === 0)) {
    message.error('Campaign order category is required');
    return false;
  }

  // Check if advertiser exists
  if (!advertiser) {
    message.error('Campaign order advertiser is required');
    return false;
  }

  // Check if advertiserType exists
  if (!advertiserType) {
    message.error('Campaign order advertiser type is required');
    return false;
  }

  // Check if campaigns exist (campaigns ARE the line items in this data structure)
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    message.error('Campaign order must have at least one line item');
    return false;
  }

  return true;
};

// Validates a specific campaign for launch (campaign order fields + single campaign)
export const validateCampaignForLaunch = (currentCampaignOrder, campaignIndex, message) => {
  console.log('=== validateCampaignForLaunch START ===');
  console.log('campaignIndex:', campaignIndex);
  console.log('currentCampaignOrder:', currentCampaignOrder);

  // First validate campaign order level fields
  console.log('Validating campaign order fields...');
  if (!validateCampaignOrderFields(currentCampaignOrder, message)) {
    console.log('❌ Campaign order fields validation FAILED');
    return false;
  }
  console.log('✅ Campaign order fields validation PASSED');

  const { campaigns } = currentCampaignOrder;

  // Validate campaign index is valid
  console.log('Validating campaign index...', { campaignIndex, campaignsLength: campaigns.length });
  if (campaignIndex < 0 || campaignIndex >= campaigns.length) {
    console.log('❌ Invalid campaign index');
    message.error('Invalid campaign index');
    return false;
  }

  const campaign = campaigns[campaignIndex];
  console.log('Campaign to validate:', campaign);

  if (!campaign) {
    console.log('❌ Campaign is missing');
    message.error(`Campaign ${campaignIndex + 1} is missing`);
    return false;
  }

  // Check if campaign has lineItems array (new nested structure)
  const lineItems = campaign.lineItems || [campaign];
  const hasNestedStructure = campaign.lineItems && Array.isArray(campaign.lineItems);
  console.log('Line items structure:', { hasNestedStructure, lineItemsCount: lineItems.length });

  // Validate each line item within this specific campaign
  for (let j = 0; j < lineItems.length; j++) {
    const lineItem = lineItems[j];
    const lineItemLabel = hasNestedStructure ? `line item ${j + 1} in campaign ${campaignIndex + 1}` : `line item ${campaignIndex + 1}`;
    console.log(`\nValidating ${lineItemLabel}...`);
    console.log('Line item data:', lineItem);

    if (!lineItem) {
      console.log(`❌ Line item is missing for ${lineItemLabel}`);
      message.error(`Line item is missing for ${lineItemLabel}`);
      return false;
    }

    // Check if line item name is provided
    console.log('Checking line item name...', lineItem.name);
    if (!lineItem.name || lineItem.name.trim() === '') {
      console.log(`❌ Name is required for ${lineItemLabel}`);
      message.error(`Name is required for ${lineItemLabel}`);
      return false;
    }

    // Check if ad group exists
    console.log('Checking ad group...', lineItem.adGroup);
    if (!lineItem.adGroup) {
      console.log(`❌ Ad Group is required for ${lineItemLabel}`);
      message.error(`Ad Group is required for ${lineItemLabel}`);
      return false;
    }

    // Check if ad group has creatives
    console.log('Checking ad group creatives...', lineItem.adGroup.adGroupCreatives);
    if (!lineItem.adGroup.adGroupCreatives || !Array.isArray(lineItem.adGroup.adGroupCreatives) || lineItem.adGroup.adGroupCreatives.length === 0) {
      console.log(`❌ Ad Group must have at least one creative for ${lineItemLabel}`);
      message.error(`Ad Group must have at least one creative for ${lineItemLabel}`);
      return false;
    }

    // Validate each ad group creative
    for (let k = 0; k < lineItem.adGroup.adGroupCreatives.length; k++) {
      const creative = lineItem.adGroup.adGroupCreatives[k];

      if (!creative) {
        message.error(`Creative ${k + 1} is missing in ad group for ${lineItemLabel}`);
        return false;
      }

      // Check if creative name is provided
      if (!creative.name || creative.name.trim() === '') {
        message.error(`Creative name is required for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }

      // Check if clickthrough URL is provided
      if (!creative.clickthrough || creative.clickthrough.trim() === '') {
        message.error(`Clickthrough URL is required for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }

      // Check if creative has medias
      if (!creative.medias || !Array.isArray(creative.medias) || creative.medias.length === 0) {
        message.error(`Creative must have at least one media for creative ${k + 1} in ${lineItemLabel}`);
        return false;
      }
    }

    // Check if start date exists and is valid
    if (!lineItem.startDate) {
      message.error(`Start date is required for ${lineItemLabel}`);
      return false;
    }

    // Check if end date exists and is valid
    if (!lineItem.endDate) {
      message.error(`End date is required for ${lineItemLabel}`);
      return false;
    }

    // Check if line item start date is before end date
    if (moment(lineItem.startDate).isSameOrAfter(moment(lineItem.endDate))) {
      message.error(`Start date must be before end date for ${lineItemLabel}`);
      return false;
    }

    // Check if line item CPM is a valid positive number
    if (!lineItem.cpm || lineItem.cpm <= 0 || isNaN(lineItem.cpm)) {
      message.error(`CPM must be a valid positive number for ${lineItemLabel}`);
      return false;
    }

    // Check if line item budget is a valid positive number (using budgetTotal)
    if (!lineItem.budgetTotal || lineItem.budgetTotal <= 0 || isNaN(lineItem.budgetTotal)) {
      message.error(`Budget must be a valid positive number for ${lineItemLabel}`);
      return false;
    }

    // Check if targeting type exists
    if (!lineItem.targetingType) {
      message.error(`Targeting type is required for ${lineItemLabel}`);
      return false;
    }

    // Check if targeting data exists based on targeting type
    if (lineItem.targetingType === 'REGION' && (!lineItem.targetingRegions || lineItem.targetingRegions.length === 0)) {
      message.error(`Targeting regions are required for ${lineItemLabel}`);
      return false;
    }

    if (lineItem.targetingType === 'LOCAL' && (!lineItem.targetingLocations || lineItem.targetingLocations.length === 0)) {
      message.error(`Targeting locations are required for ${lineItemLabel}`);
      return false;
    }

    if (lineItem.targetingType === 'COUNTRY' && (!lineItem.targetingCountries || lineItem.targetingCountries.length === 0)) {
      console.log(`❌ Targeting countries are required for ${lineItemLabel}`);
      message.error(`Targeting countries are required for ${lineItemLabel}`);
      return false;
    }

    console.log(`✅ ${lineItemLabel} validation PASSED`);
  }

  console.log('✅ validateCampaignForLaunch COMPLETE - ALL VALIDATIONS PASSED');
  return true;
};

// Pre-flight validation function to check campaign order fields and ALL campaigns
export const validateCampaignOrder = (currentCampaignOrder, message) => {
  const { campaigns } = currentCampaignOrder;

  // First validate campaign order level fields
  if (!validateCampaignOrderFields(currentCampaignOrder, message)) {
    return false;
  }

  // Validate each campaign (which are actually line items)
  for (let i = 0; i < campaigns.length; i++) {
    const campaign = campaigns[i];

    if (!campaign) {
      message.error(`Campaign ${i + 1} is missing`);
      return false;
    }

    // Check if campaign has lineItems array (new nested structure)
    const lineItems = campaign.lineItems || [campaign];
    const hasNestedStructure = campaign.lineItems && Array.isArray(campaign.lineItems);

    // Validate each line item within the campaign
    for (let j = 0; j < lineItems.length; j++) {
      const lineItem = lineItems[j];
      const lineItemLabel = hasNestedStructure ? `line item ${j + 1} in campaign ${i + 1}` : `line item ${i + 1}`;

      if (!lineItem) {
        message.error(`Line item is missing for ${lineItemLabel}`);
        return false;
      }

      // Check if line item name is provided
      if (!lineItem.name || lineItem.name.trim() === '') {
        message.error(`Name is required for ${lineItemLabel}`);
        return false;
      }

      // Check if ad group exists
      if (!lineItem.adGroup) {
        message.error(`Ad Group is required for ${lineItemLabel}`);
        return false;
      }

      // Check if ad group has creatives
      if (!lineItem.adGroup.adGroupCreatives || !Array.isArray(lineItem.adGroup.adGroupCreatives) || lineItem.adGroup.adGroupCreatives.length === 0) {
        message.error(`Ad Group must have at least one creative for ${lineItemLabel}`);
        return false;
      }

      // Validate each ad group creative
      for (let k = 0; k < lineItem.adGroup.adGroupCreatives.length; k++) {
        const creative = lineItem.adGroup.adGroupCreatives[k];

        if (!creative) {
          message.error(`Creative ${k + 1} is missing in ad group for ${lineItemLabel}`);
          return false;
        }

        // Check if creative name is provided
        if (!creative.name || creative.name.trim() === '') {
          message.error(`Creative name is required for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }

        // Check if clickthrough URL is provided
        if (!creative.clickthrough || creative.clickthrough.trim() === '') {
          message.error(`Clickthrough URL is required for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }

        // Check if creative has medias
        if (!creative.medias || !Array.isArray(creative.medias) || creative.medias.length === 0) {
          message.error(`Creative must have at least one media for creative ${k + 1} in ${lineItemLabel}`);
          return false;
        }
      }

      // Check if start date exists and is valid
      if (!lineItem.startDate) {
        message.error(`Start date is required for ${lineItemLabel}`);
        return false;
      }

      // Check if end date exists and is valid
      if (!lineItem.endDate) {
        message.error(`End date is required for ${lineItemLabel}`);
        return false;
      }

      // Check if line item start date is before end date
      if (moment(lineItem.startDate).isSameOrAfter(moment(lineItem.endDate))) {
        message.error(`Start date must be before end date for ${lineItemLabel}`);
        return false;
      }

      // Check if line item CPM is a valid positive number
      if (!lineItem.cpm || lineItem.cpm <= 0 || isNaN(lineItem.cpm)) {
        message.error(`CPM must be a valid positive number for ${lineItemLabel}`);
        return false;
      }

      // Check if line item budget is a valid positive number (using budgetTotal)
      if (!lineItem.budgetTotal || lineItem.budgetTotal <= 0 || isNaN(lineItem.budgetTotal)) {
        message.error(`Budget must be a valid positive number for ${lineItemLabel}`);
        return false;
      }

      // Check if targeting type exists
      if (!lineItem.targetingType) {
        message.error(`Targeting type is required for ${lineItemLabel}`);
        return false;
      }

      // Check if targeting data exists based on targeting type
      if (lineItem.targetingType === 'REGION' && (!lineItem.targetingRegions || lineItem.targetingRegions.length === 0)) {
        message.error(`Targeting regions are required for ${lineItemLabel}`);
        return false;
      }

      if (lineItem.targetingType === 'LOCAL' && (!lineItem.targetingLocations || lineItem.targetingLocations.length === 0)) {
        message.error(`Targeting locations are required for ${lineItemLabel}`);
        return false;
      }

      if (lineItem.targetingType === 'COUNTRY' && (!lineItem.targetingCountries || lineItem.targetingCountries.length === 0)) {
        message.error(`Targeting countries are required for ${lineItemLabel}`);
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Comprehensive cleaning function for line items during cloning operations
 * Recursively removes database IDs and references while preserving user data
 */
export const cleanLineItemsForCloning = (campaignOrder) => {
  console.log('=== CLEANING LINE ITEMS FOR CLONING ===');
  console.log('Original campaign order:', campaignOrder);

  if (!campaignOrder || !campaignOrder.campaigns || !campaignOrder.campaigns.length) {
    console.log('No campaigns found to clean');
    return campaignOrder;
  }

  const cleanedCampaignOrder = { ...campaignOrder };

  // Clean campaigns array
  cleanedCampaignOrder.campaigns = campaignOrder.campaigns.map((campaign, campaignIndex) => {
    console.log(`Cleaning campaign ${campaignIndex}:`, campaign);

    const cleanedCampaign = { ...campaign };

    // Remove campaign-level database fields
    delete cleanedCampaign.id;
    delete cleanedCampaign.createdAt;
    delete cleanedCampaign.updatedAt;
    delete cleanedCampaign.__typename;
    delete cleanedCampaign.adProviderId;

    // Clean line items
    if (campaign.lineItems && campaign.lineItems.length > 0) {
      console.log(`Found ${campaign.lineItems.length} line items to clean`);

      cleanedCampaign.lineItems = campaign.lineItems.map((lineItem, lineItemIndex) => {
        console.log(`Cleaning line item ${lineItemIndex}:`, lineItem.name || `Line Item ${lineItemIndex + 1}`);

        const cleanedLineItem = { ...lineItem };

        // Remove line item database fields
        delete cleanedLineItem.id;
        delete cleanedLineItem.createdAt;
        delete cleanedLineItem.updatedAt;
        delete cleanedLineItem.__typename;

        // Clean targeting regions
        if (cleanedLineItem.targetingRegions && Array.isArray(cleanedLineItem.targetingRegions)) {
          cleanedLineItem.targetingRegions = cleanedLineItem.targetingRegions.map(region => {
            const cleanedRegion = { ...region };
            delete cleanedRegion.id;
            delete cleanedRegion.createdAt;
            delete cleanedRegion.updatedAt;
            delete cleanedRegion.__typename;
            return cleanedRegion;
          });
        }

        // Clean targeting locations
        if (cleanedLineItem.targetingLocations && Array.isArray(cleanedLineItem.targetingLocations)) {
          cleanedLineItem.targetingLocations = cleanedLineItem.targetingLocations.map(location => {
            const cleanedLocation = { ...location };
            delete cleanedLocation.id;
            delete cleanedLocation.createdAt;
            delete cleanedLocation.updatedAt;
            delete cleanedLocation.__typename;

            // Clean nested geo targets
            if (cleanedLocation.geoTargets && Array.isArray(cleanedLocation.geoTargets)) {
              cleanedLocation.geoTargets = cleanedLocation.geoTargets.map(geoTarget => {
                const cleanedGeoTarget = { ...geoTarget };
                delete cleanedGeoTarget.id;
                delete cleanedGeoTarget.__typename;

                // Clean circles
                if (cleanedGeoTarget.circles && Array.isArray(cleanedGeoTarget.circles)) {
                  cleanedGeoTarget.circles = cleanedGeoTarget.circles.map(circle => {
                    const cleanedCircle = { ...circle };
                    delete cleanedCircle.id;
                    delete cleanedCircle.__typename;

                    if (cleanedCircle.location) {
                      delete cleanedCircle.location.id;
                      delete cleanedCircle.location.__typename;
                    }
                    if (cleanedCircle.coordinate) {
                      delete cleanedCircle.coordinate.id;
                      delete cleanedCircle.coordinate.__typename;
                    }
                    return cleanedCircle;
                  });
                }

                // Clean polygons
                if (cleanedGeoTarget.polygons && Array.isArray(cleanedGeoTarget.polygons)) {
                  cleanedGeoTarget.polygons = cleanedGeoTarget.polygons.map(polygon => {
                    const cleanedPolygon = { ...polygon };
                    delete cleanedPolygon.id;
                    delete cleanedPolygon.__typename;

                    if (cleanedPolygon.location) {
                      delete cleanedPolygon.location.id;
                      delete cleanedPolygon.location.__typename;
                    }
                    return cleanedPolygon;
                  });
                }

                // Clean location
                if (cleanedGeoTarget.location) {
                  delete cleanedGeoTarget.location.id;
                  delete cleanedGeoTarget.location.__typename;
                }

                return cleanedGeoTarget;
              });
            }

            return cleanedLocation;
          });
        }

        // Clean targeting countries
        if (cleanedLineItem.targetingCountries && Array.isArray(cleanedLineItem.targetingCountries)) {
          cleanedLineItem.targetingCountries = cleanedLineItem.targetingCountries.map(country => {
            const cleanedCountry = { ...country };
            delete cleanedCountry.id;
            delete cleanedCountry.__typename;
            return cleanedCountry;
          });
        }

        // Clean first party data audiences
        if (cleanedLineItem.firstPartyDataAudiences && Array.isArray(cleanedLineItem.firstPartyDataAudiences)) {
          cleanedLineItem.firstPartyDataAudiences = cleanedLineItem.firstPartyDataAudiences.map(audience => {
            const cleanedAudience = { ...audience };
            delete cleanedAudience.id;
            delete cleanedAudience.createdAt;
            delete cleanedAudience.updatedAt;
            delete cleanedAudience.__typename;

            // Clean hashed emails
            if (cleanedAudience.hashedEmails && Array.isArray(cleanedAudience.hashedEmails)) {
              cleanedAudience.hashedEmails = cleanedAudience.hashedEmails.map(email => {
                const cleanedEmail = { ...email };
                delete cleanedEmail.id;
                delete cleanedEmail.__typename;
                return cleanedEmail;
              });
            }

            return cleanedAudience;
          });
        }

        // Clean deal IDs
        if (cleanedLineItem.dealIds && Array.isArray(cleanedLineItem.dealIds)) {
          cleanedLineItem.dealIds = cleanedLineItem.dealIds.map(deal => {
            const cleanedDeal = { ...deal };
            delete cleanedDeal.id;
            delete cleanedDeal.__typename;

            if (cleanedDeal.org) {
              delete cleanedDeal.org.id;
              delete cleanedDeal.org.__typename;
            }

            return cleanedDeal;
          });
        }

        // Clean ad group and creatives (critical for both display and video)
        if (cleanedLineItem.adGroup) {
          const cleanedAdGroup = { ...cleanedLineItem.adGroup };
          delete cleanedAdGroup.id;
          delete cleanedAdGroup.createdAt;
          delete cleanedAdGroup.updatedAt;
          delete cleanedAdGroup.__typename;

          // Clean ad group creatives
          if (cleanedAdGroup.adGroupCreatives && Array.isArray(cleanedAdGroup.adGroupCreatives)) {
            console.log(`Cleaning ${cleanedAdGroup.adGroupCreatives.length} creatives for line item ${lineItemIndex}`);

            cleanedAdGroup.adGroupCreatives = cleanedAdGroup.adGroupCreatives.map((creative, creativeIndex) => {
              console.log(`Cleaning creative ${creativeIndex}:`, creative.name || `Creative ${creativeIndex + 1}`);

              const cleanedCreative = { ...creative };
              delete cleanedCreative.id;
              delete cleanedCreative.createdAt;
              delete cleanedCreative.updatedAt;
              delete cleanedCreative.__typename;

              // Clean media files
              if (cleanedCreative.medias && Array.isArray(cleanedCreative.medias)) {
                cleanedCreative.medias = cleanedCreative.medias.map(media => {
                  const cleanedMedia = { ...media };
                  delete cleanedMedia.id;
                  delete cleanedMedia.createdAt;
                  delete cleanedMedia.updatedAt;
                  delete cleanedMedia.__typename;
                  return cleanedMedia;
                });
              }

              return cleanedCreative;
            });
          }

          cleanedLineItem.adGroup = cleanedAdGroup;
        }

        console.log(`Cleaned line item ${lineItemIndex}:`, cleanedLineItem);
        return cleanedLineItem;
      });

      console.log(`Campaign ${campaignIndex} cleaning complete. Line items count: ${cleanedCampaign.lineItems.length}`);
    } else {
      console.log(`Campaign ${campaignIndex} has no line items to clean`);
      cleanedCampaign.lineItems = [];
    }

    return cleanedCampaign;
  });

  console.log('=== LINE ITEMS CLEANING COMPLETE ===');
  console.log('Cleaned campaign order:', cleanedCampaignOrder);

  return cleanedCampaignOrder;
};
